import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from 'view/pages/auth/hooks/useAuth';
import { AuthLayout, UserProfileLayout, CreateJobPostLayout } from 'view/layouts';
import {
  CheckEmailPage,
  ForgotPasswordPage,
  LoginPage,
  OnboardingPage,
  RegistrationPage,
  ResetPasswordPage,
  VerifyEmailPage,
  CreatePasswordPage,
  ChooseYourRolePage,
  ChangeEmailPage,
} from 'view/pages/auth';
import {
  NotificationsSettings,
  FAQ,
  ProfileSettings,
  WorkPreferences,
  AccountSettings,
  CompanyMemberships,
  TestPage,
  JobPostsListPage,
  ApplicationsPage,
} from 'view/pages/profile';
import React, { memo, useEffect, useState } from 'react';
import { createContext, useContext, useContextSelector as useCS } from 'use-context-selector';
import { showSystemToast } from 'view/components/SystemToast';
import { createStore } from 'little-state-machine';
import { Containers } from './domain/Containers';
import {
  JobDetails,
  JobInformation,
  QualificationsAndSkills,
  ApplicationInstructions,
  JobPostCardPreview,
  ViewJobPostPage,
} from './view/pages/createJobPost';
import CompanySetup from './view/pages/createJobPost/CompanySetup/index';
import {
  Applicants,
  CandidateList,
  CandidateProfile,
  FavoriteCandidatesPage,
  MyJobPostsPage,
} from './view/pages/candidates';
import { ChatsPage } from './view/pages/messenger';
import { CompanyProfile } from './view/pages/public/CompanyProfile';
import { CompaniesListPage } from './view/pages/public/CompaniesListPage';
import VerifyMembershipEmail from './view/pages/memberships/VerifyMembershipEmail/index';
import { useNotificationsSocked } from './view/pages/profile/hooks/useNotificationsSocked';
import { storageService } from './infrastructure/services/StorageService';
import { TUpdate } from './domain/types';
import {
  GlobalConfigContext,
  TGlobalConfigContextState,
} from './domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from './domain/globalConfig/selectors';
import SocialLoginPage from './view/pages/socialLogin';
import { PROFILE_NOTIFICATIONS_PER_PAGE } from './view/pages/profile/hooks/useGetProfileNotifications';
import Page404 from './view/pages/page404';
import { useChatSocket } from './view/pages/messenger/hooks/useChatSocked';
import { SmartRedirect } from './view/components/SmartRedirect';
// Public job browsing now uses the same components as authenticated users

export const initState = {
  data: {
    // step1
    industry: null,
    jobCategory: null,
    jobTitle: null,
    positionType: null,
    location: null,
    jobLocation: {
      onSite: false,
      remote: false,
      hybrid: false,
    },
    // step2
    experienceLevel: null,
    experienceYearsMin: '',
    experienceYearsMax: '',
    salaryMin: '',
    salaryMax: '',
    languages: null,
    education: null,
    rolesAndResponsibilities: '',
    // step3
    qualifications: [],
    skills: [],
    // step5
    applicationProcess: '',
    immHiringNeed: { id: 2, name: 'No' },
    diversityStatement: '',
    faq: [],

    postStatus: '',

    progressStep: 1,
  },
};

createStore(initState);

export const stylesTerms = {
  width: '100vw',
  height: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: '2rem',
};

const ChatSocketContext = createContext<ReturnType<typeof useChatSocket> | null>(null);

export const useChatSocketContext = () => {
  const context = useContext(ChatSocketContext);
  if (!context) {
    throw new Error('useChatSocketContext must be used within a ChatSocketProvider');
  }
  return context;
};

export const ChatSocketProvider = ({
  children,
  token,
}: {
  children: React.ReactNode;
  token: any;
}) => {
  const socket = useChatSocket({ token });

  return <ChatSocketContext.Provider value={socket}>{children}</ChatSocketContext.Provider>;
};

const ProtectedRoute = memo(() => {
  const globalUpdate: TUpdate<TGlobalConfigContextState> | undefined = useCS(
    GlobalConfigContext,
    globalConfigSelectors.update
  );

  const profileNotificationsData: any = useCS(
    GlobalConfigContext,
    globalConfigSelectors.profileNotificationsData
  );

  const userData = useCS(GlobalConfigContext, globalConfigSelectors.userData);
  const isOpenOnboardingWizard = useCS(
    GlobalConfigContext,
    globalConfigSelectors.isOpenOnboardingWizard
  );

  const { isAuthenticated, isLoading } = useAuth();
  const [token, setToken] = useState<string | undefined>(undefined);

  const { isSocketConnected, subscribeToEvents, unsubscribeToEvents /* emitEvent */ } =
    useNotificationsSocked({
      token,
    });

  useEffect(() => {
    const fetchToken = async () => {
      const t = await storageService.getItem('accessToken');
      setToken(t);
    };
    fetchToken();
  }, []);

  // Check if user needs onboarding and open wizard
  useEffect(() => {
    if (!isAuthenticated || !userData?.role || isOpenOnboardingWizard) return;

    const hasCompletedOnboarding =
      userData?.jobSeekerProfile?.hasWorkPreferencesFilled ||
      userData?.recruiterProfile?.hasWorkPreferencesFilled;

    if (!hasCompletedOnboarding && globalUpdate) {
      globalUpdate({ isOpenOnboardingWizard: true });
    }
  }, [isAuthenticated, userData, isOpenOnboardingWizard, globalUpdate]);

  useEffect(() => {
    if (!isSocketConnected && !isAuthenticated) return;

    subscribeToEvents('unreadNotifications', (data) => {
      if (globalUpdate) {
        const unreadCount = data?.count ?? 0;
        const notificationText = `You have ${unreadCount} new notification${unreadCount !== 1 ? 's' : ''}. Tap to view.`;
        globalUpdate({ unreadNotificationsCount: data?.count });
        if (data?.count) showSystemToast?.('info', 'Notifications', notificationText);
      }
    });

    subscribeToEvents('notification', (data) => {
      if (globalUpdate) {
        globalUpdate({
          profileNotificationsData: {
            data: [data, ...profileNotificationsData.data],
            limit: PROFILE_NOTIFICATIONS_PER_PAGE,
            page: 1, // Сбрасываем на первую страницу
            total: +profileNotificationsData.total + 1,
          },
        });
      }
    });

    // eslint-disable-next-line consistent-return
    return () => {
      unsubscribeToEvents('unreadNotifications');
      unsubscribeToEvents('notification');
    };
  }, [isSocketConnected, isAuthenticated, profileNotificationsData]);

  if (isLoading) return null;

  return isAuthenticated ? (
    <ChatSocketProvider token={token}>
      <Outlet />
    </ChatSocketProvider>
  ) : (
    <Navigate to="/auth/onboarding" replace />
  );
});

const AuthRedirect = memo(() => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return null;

  if (
    isAuthenticated &&
    location.pathname !== '/auth/create-password' &&
    location.pathname !== '/auth/choose-your-role' &&
    location.pathname !== '/auth/terms-of-use' &&
    location.pathname !== '/auth/privacy-policy' &&
    location.pathname !== '/auth/change-email'
  ) {
    return <Navigate to="/profile/profile-settings" replace />;
  }

  return <Outlet />;
});

const App = memo(() => {
  return (
    <BrowserRouter>
      <Containers />
      <Routes>
        {/* SMART DEFAULT REDIRECT - role-based routing */}
        <Route path="/" element={<SmartRedirect />} />

        {/* PUBLIC ROUTES - Use unified components that work for both auth and non-auth */}
        <Route path="/jobs" element={<JobPostsListPage />} />
        <Route path="/jobs/:id" element={<ViewJobPostPage />} />
        <Route path="/companies" element={<CompaniesListPage />} />
        <Route path="/companies/:slug" element={<CompanyProfile />} />
        <Route path="/memberships/verify-email" element={<VerifyMembershipEmail />} />

        {/* Existing auth routes */}
        <Route path="/auth" element={<Navigate to="/auth/onboarding" />} />

        <Route path="/auth" element={<AuthRedirect />}>
          <Route path="socialLogin" element={<SocialLoginPage />} />
          <Route
            path="privacy-policy"
            element={<div style={stylesTerms}>Privacy Policy Page</div>}
          />
          <Route element={<AuthLayout />}>
            <Route path="onboarding" element={<OnboardingPage />} />
            <Route path="login" element={<LoginPage />} />
            <Route path="forgot" element={<ForgotPasswordPage />} />
            <Route path="forgot/check-email" element={<CheckEmailPage />} />
            {/* Set new password Page after forgot form(email) */}
            <Route path="reset-password" element={<ResetPasswordPage />} />
            {/* // Create Account page */}
            <Route path="registration" element={<RegistrationPage />} />
            <Route path="registration/verify-email" element={<VerifyEmailPage />} />
            <Route path="create-password" element={<CreatePasswordPage />} />
            <Route path="choose-your-role" element={<ChooseYourRolePage />} />
            <Route path="change-email" element={<ChangeEmailPage />} />
          </Route>
        </Route>

        {/* Public route for shared job post links */}
        <Route path="/profile/view-job-post" element={<ViewJobPostPage />} />

        <Route path="/profile" element={<ProtectedRoute />}>
          <Route element={<UserProfileLayout />}>
            <Route path="profile-settings" element={<ProfileSettings />} />
            <Route path="work-preferences" element={<WorkPreferences />} />
            <Route path="account-settings" element={<AccountSettings />} />
            <Route path="notifications-settings" element={<NotificationsSettings />} />
            <Route path="company-memberships" element={<CompanyMemberships />} />
            <Route path="help-center" element={<FAQ />} />
            <Route path="test" element={<TestPage />} />
          </Route>
          <Route path="job-post-preview" element={<JobPostCardPreview />} />
          <Route path="job-list" element={<JobPostsListPage />} />
          <Route path="applications" element={<ApplicationsPage />} />
        </Route>

        <Route path="/create-job-post" element={<ProtectedRoute />}>
          <Route element={<CreateJobPostLayout />}>
            <Route path="setup" element={<CompanySetup />} />
            <Route path="step1" element={<JobInformation />} />
            <Route path="step2" element={<JobDetails />} />
            <Route path="step3" element={<QualificationsAndSkills />} />

            <Route path="step5" element={<ApplicationInstructions />} />
          </Route>
        </Route>

        <Route path="/candidates" element={<ProtectedRoute />}>
          <Route path="candidates-list" element={<CandidateList />} />
          <Route path="candidate-profile" element={<CandidateProfile />} />
          <Route path="favorites" element={<FavoriteCandidatesPage />} />
          <Route path="my-job-posts" element={<MyJobPostsPage />} />
          <Route path="applicants" element={<Applicants />} />
        </Route>

        <Route path="/messenger" element={<ProtectedRoute />}>
          <Route path="chats" element={<ChatsPage />} />
        </Route>

        <Route path="/not-found" element={<ProtectedRoute />}>
          <Route path="404" element={<Page404 />} />
        </Route>

        {/* Fallback - redirect to smart redirect */}
        <Route path="*" element={<SmartRedirect />} />
      </Routes>
    </BrowserRouter>
  );
});

export default App;
